/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {makeStyles} from '@material-ui/core/styles';
import Render from '@ion/dls/src/render/Render';
import {StatusIcon, StatusIconVariant} from '../../icons/StatusIcon';
import {IconStatus} from '../../types/types';

const useStyles = makeStyles(theme => ({
  indicatorText : {
    fontSize   : '14px',
    fontFamily : theme?.typography?.fontFamily,
    lineHeight : '20px'
  },
  pulseWrapper : {
    position    : 'relative',
    display     : 'inline-flex',
    height      : 18,
    width       : 18,
    alignItems  : 'center',
    marginRight : 3
  },
  pulseRing : {
    position        : 'absolute',
    inset           : 0,
    borderRadius    : '50%',
    backgroundColor : 'currentColor',
    animation       : '$pulse 2s ease-out infinite'
  },
  '@keyframes pulse' : {
    '0%' : {
      opacity   : 0.75,
      transform : 'scale(0.5)'
    },
    '100%' : {
      opacity   : 0,
      transform : 'scale(1.25)'
    }
  }
}));

export interface StatusIndicatorType {
  text?: string;
  type: IconStatus;
  variant?: StatusIconVariant;
  pulse?: boolean;
  isPendingMode?: boolean;
}

/**
 * StatusIndicator displays a colored status icon with optional label text, a pulse animation,
 * and a pending state. The icon color is driven by the `type` prop (`IconStatus` enum value).
 *
 * ```typescript
 * import {StatusIndicator} from '@ion/dls/src/components/status/StatusIndicator';
 *
 * <StatusIndicator type={IconStatus.active} text={translate('basic:translation')} />
 * <StatusIndicator type={IconStatus.active} pulse />
 * <StatusIndicator type={IconStatus.idle} isPendingMode />
 * ```
 *
 * @param type          - The status type that determines the icon color.
 * @param text          - Optional label text displayed to the right of the icon.
 * @param variant       - Optional icon variant. Renders filled or outlined.
 * @param pulse         - Optional flag to show an expanding ring animation behind the icon.
 * @param isPendingMode - Optional flag to hide the icon while data is loading.
 */
export const StatusIndicator = ({
  text,
  type,
  variant,
  pulse,
  isPendingMode
}: StatusIndicatorType) => {
  const classes = useStyles();
  return (
    <>
      <span className={pulse ? classes.pulseWrapper : ''}
        style={{color: type as unknown as string}}>
        <Render.If condition={!isPendingMode}>
          <StatusIcon type={type} variant={variant}/>
        </Render.If>
        <Render.If condition={!!pulse}>
          <span className={classes.pulseRing} />
        </Render.If>
      </span>
      <Render.If condition={!!text}>
        <span className={classes.indicatorText}>{text}</span>
      </Render.If>
    </>
  );
};

export default StatusIndicator;
